"use client";

import { useRef } from "react";
import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";
import { PageFuturesSchema } from "@/sanity/pages/PageFuturesTemplate";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import CryptoPillar from "@/components/global/ui/CryptoPillar";
import { CTAButton } from "@/shadcn/ui/button";
import { motion, useScroll, useTransform } from "framer-motion";

import bg from "@/public/global/bg-art.jpg";
import knight from "@/public/global/knight.png";
import futuresHero from "@/public/futures/hero.png";

export default function HeroSection({
  data,
  className,
  showTopBanner,
  futures = false,
}: {
  data: PageHomeSchema | PageFuturesSchema;
  className?: string;
  showTopBanner?: boolean;
  futures?: boolean;
}) {
  const parallaxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: parallaxRef,
    offset: ["start end", "end start"],
  });

  const yEthereum = useTransform(scrollYProgress, [0, 1], ["-3%", "3%"]);
  const ySolana = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const yBitcoin = useTransform(scrollYProgress, [0, 1], ["5%", "-5%"]);
  const yKnight = useTransform(scrollYProgress, [0, 1], ["-2%", "2%"]);

  return (
    <section
      id="hero-section"
      ref={parallaxRef}
      className={cn(
        "relative overflow-clip px-[2rem] lg:px-[5rem] pt-[2rem] lg:pt-[5rem]",
        showTopBanner && "mt-[16rem] lg:mt-[7rem]",
        className,
      )}
    >
      <div className="relative overflow-clip w-[min(229.25rem,100%)] mx-auto rounded-[2rem] lg:rounded-[5rem] pt-[13rem] lg:pt-[18.75rem]">
        <div className={cn("absolute top-0 left-0 w-full h-full bg-gradient-to-b", futures ? "from-[#FBFBFB] -from-[34%] to-[#E6E7F2]" : "from-[#F6F9F7] to-[#E6F2EB]")}>
          <Image
            className="absolute left-1/2 -translate-x-1/2 bottom-0 !w-[max(100%,90rem)] !max-w-none !h-full object-cover object-top mix-blend-luminosity opacity-[.36]"
            src={bg}
            quality={100}
            alt="background art"
          />
        </div>

        <div className="container flex relative z-10 flex-col items-center px-[3rem]">
          {data?.heroChip && (
            <p className="font-medium text-black/70 mb-[1.5rem] text-[1.75rem] lg:mb-[2rem] lg:text-[2rem]">
              {data.heroChip}
            </p>
          )}

          {data?.heroText && (
            <div className={cn("richtext [&_br]:hidden lg:[&_br]:block", data?.settings?.slug?.current?.endsWith("/br") ? "[&_br]:block" : "[&_br]:hidden")}>
              <PortableText
                value={data.heroText}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.heroCTA && (
            <CTAButton className="mt-[3rem]" data={data.heroCTA} />
          )}

          {/* {!futures && (
            <>
              <CryptoPillar
                className="left-[calc(50%-21rem)] lg:left-[calc(50%-38rem)] h-[25rem] !scale-[.75] lg:!scale-100"
                type="ethereum"
                style={{ y: yEthereum }}
                trailColors="bg-gradient-to-b from-white to-[#FBFBFB]/0"
              />
              <CryptoPillar
                className="bottom-[5rem] lg:bottom-0 left-[calc(50%+6rem)] lg:left-[calc(50%+2rem)] h-[43rem] lg:h-[57.5rem] !scale-[.8] lg:!scale-[1.16]"
                type="solana"
                style={{ y: ySolana }}
                trailColors="bg-gradient-to-b from-white to-[#FBFBFB]/0"
              />
              <CryptoPillar
                className="bottom-[11rem] lg:bottom-0 left-[calc(50%+16rem)] lg:left-[calc(50%+25rem)] h-[20rem] lg:h-[35rem] !scale-[.75] lg:!scale-[1.08]"
                type="bitcoin"
                style={{ y: yBitcoin }}
                trailColors="bg-gradient-to-b from-white to-[#FBFBFB]/0"
              />
            </>
          )} */}

          <motion.div
            className={cn(
              "relative z-10",
              futures
                ? "w-[min(97.625rem,100%)] mt-[2rem] lg:mt-0"
                : "w-[min(52.5rem,100%)] lg:w-[min(59.5rem,100%)] translate-x-[3rem] lg:translate-x-0 mt-[5rem] lg:mt-[10rem]"
            )}
            style={{ y: yKnight }}
          >
            <Image
              className="!w-full"
              src={futures ? futuresHero : knight}
              quality={100}
              alt="statue"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
